/**
 * Tiny helpers used by visual components.
 */
import React from "react";
import { flagFromCode } from "./countries";

/** Deterministic FNV-1a hash → 32-bit unsigned int. */
function hash32(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * Map a player to a 1–99 "rating".
 * - Prefer player.fame_score if present (scaled into 70–95).
 * - Else: deterministic hash of name → 70–95.
 */
export function deriveRating(player) {
  if (!player) return 75;
  if (typeof player.fame_score === "number" && player.fame_score > 0) {
    const f = Math.min(100, Math.max(0, player.fame_score));
    return Math.round(70 + (f / 100) * 25);
  }
  const seed = player.name || player._id || "x";
  const h = hash32(seed);
  return 70 + (h % 26); // 70..95
}

/** Difficulty pill colour map. */
export const DIFFICULTY_COLORS = {
  medium: "bg-[var(--accent)]/10 text-[var(--accent)] border-[var(--accent)]/30",
  hard: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  insane: "bg-red-500/10 text-red-300 border-red-500/40",
};

/** Common country name → ISO-3166 alpha-2 code, for flag emoji. */
const COUNTRY_TO_CC = {
  Argentina: "AR", Brazil: "BR", France: "FR", Germany: "DE", Spain: "ES",
  Italy: "IT", Portugal: "PT", Netherlands: "NL", Belgium: "BE", Croatia: "HR",
  Uruguay: "UY", Colombia: "CO", Chile: "CL", Mexico: "MX", "United States": "US",
  USA: "US", England: "GB-ENG", Scotland: "GB-SCT", Wales: "GB-WLS", "Northern Ireland": "GB-NIR",
  "United Kingdom": "GB", Ireland: "IE", Sweden: "SE", Norway: "NO", Denmark: "DK",
  Poland: "PL", Russia: "RU", Ukraine: "UA", Turkey: "TR", Greece: "GR",
  Switzerland: "CH", Austria: "AT", Serbia: "RS", Czechia: "CZ", "Czech Republic": "CZ",
  Slovakia: "SK", Slovenia: "SI", Bulgaria: "BG", Romania: "RO", Hungary: "HU",
  Egypt: "EG", Morocco: "MA", Algeria: "DZ", Tunisia: "TN", Nigeria: "NG",
  Ghana: "GH", Senegal: "SN", "Ivory Coast": "CI", Cameroon: "CM", "South Africa": "ZA",
  Japan: "JP", "South Korea": "KR", China: "CN", Australia: "AU", "Saudi Arabia": "SA",
  Iran: "IR", Iraq: "IQ", Israel: "IL", Lebanon: "LB", Qatar: "QA",
  Ecuador: "EC", Peru: "PE", Paraguay: "PY", Bolivia: "BO", Venezuela: "VE",
  "Costa Rica": "CR", Panama: "PA", Honduras: "HN", Jamaica: "JM",
  Bosnia: "BA", "Bosnia and Herzegovina": "BA", Albania: "AL", "North Macedonia": "MK",
  Finland: "FI", Iceland: "IS", Estonia: "EE", Latvia: "LV", Lithuania: "LT",
  Canada: "CA", Cuba: "CU", "Republic of Ireland": "IE",
};

/** Country name → SVG flag node (or null if unknown). Handles GB sub-nations
 *  (England/Scotland/Wales/Northern Ireland) via flag-icons' fi-gb-{eng|sct|wls|nir}
 *  classes so they render as SVGs cross-platform (Windows too). For ISO alpha-2
 *  codes it delegates to lib/countries.flagFromCode which returns a React node. */
export function countryFlag(country) {
  if (!country) return null;
  const s = String(country).trim();
  // ISO alpha-2 (and our custom KU) path — preferred since Phase 5.3.
  if (s.length === 2 && /^[A-Za-z]{2}$/.test(s)) {
    return flagFromCode(s);
  }
  // Legacy full-name fallback (kept for any cached data the client may still hold).
  const cc = COUNTRY_TO_CC[s];
  if (!cc) return null;
  if (cc.startsWith("GB-")) {
    const sub = cc.slice(3).toLowerCase();  // eng | sct | wls | nir
    return (
      <span
        role="img"
        aria-label={`${s} flag`}
        title={s}
        data-testid={`flag-GB-${sub.toUpperCase()}`}
        className={`fi fi-gb-${sub}`}
        style={{
          display: "inline-block",
          width: "1.4em",
          height: "calc(1.4em * 0.75)",
          borderRadius: 2,
          verticalAlign: "middle",
          backgroundSize: "cover",
          backgroundPosition: "center",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.08)",
        }}
      />
    );
  }
  if (cc.length !== 2) return null;
  return flagFromCode(cc);
}

/** Format a stint range like 2009–2018 (or single year if equal). */
export function yearRange(start, end) {
  if (!start) return "";
  if (!end || end === start) return String(start);
  return `${start}\u2013${end}`;
}

/** Format milliseconds as "1.23s" or "234ms". */
export function fmtMs(ms) {
  if (!ms && ms !== 0) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
}

/** Animate a number from `from` to `to` over `duration` ms via callback. */
export function tweenNumber(from, to, duration, cb) {
  const start = performance.now();
  function step(now) {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
    cb(Math.round(from + (to - from) * eased));
    if (t < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}
